"use client";

import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Copy, Download, Eye, AlertTriangle, Waypoints } from "lucide-react";
import { toast } from "sonner";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusBadge } from "./status-badge";
import { TimestampCell } from "./timestamp-cell";
import { ColumnVisibilityMenu } from "./column-visibility-menu";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CircularLoading } from "@/components/shared/circular-loading";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { LOCAL_STORAGE_KEYS } from "@/lib/constants";
import { ALLOWED_PAGE_SIZES, type SortDirection, type SortableField } from "@/types/filters";
import type { SerializedLogRecord } from "@/types/logs";
import { cn } from "@/lib/utils";
import { buildExportFilename, buildLogsCsv } from "@/utils/export-utils";
import { buildPaginationItems } from "@/utils/pagination";

function downloadRecordsCsv(records: SerializedLogRecord[]) {
  const csv = buildLogsCsv(records);
  const filename = buildExportFilename("dynamodb-dashboard-logs-page");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// const SORTABLE_COLUMNS = new Set<SortableField>(["statusCode", "route", "userId", "method"]);
const SORTABLE_COLUMNS = new Set<SortableField>([])

interface LogsTableProps {
  records: SerializedLogRecord[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onRowClick: (id: string) => void;
  sortBy: SortableField | undefined;
  sortDirection: SortDirection | undefined;
  onSortChange: (sortBy: SortableField, sortDirection: SortDirection) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  knownPageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
  resultMayBePartial: boolean;
  routeGroupingEnabled?: boolean;
  /** True only while total page count is still being resolved — not during page navigation. */
  isPageCountLoading?: boolean;
}

export function LogsTable({
  records,
  isLoading,
  error,
  onRetry,
  onRowClick,
  sortBy,
  sortDirection,
  onSortChange,
  pageSize,
  onPageSizeChange,
  currentPage,
  knownPageCount,
  hasPreviousPage,
  hasNextPage,
  onPreviousPage,
  onNextPage,
  onGoToPage,
  resultMayBePartial,
  routeGroupingEnabled = true,
  isPageCountLoading = false,
}: LogsTableProps) {
  const { copy } = useCopyToClipboard();
  const [columnVisibility, setColumnVisibility] = useLocalStorage<VisibilityState>(
    LOCAL_STORAGE_KEYS.columnVisibility,
    {},
  );

  const columns = React.useMemo<ColumnDef<SerializedLogRecord>[]>(
    () => [
      {
        id: "errorClassification",
        header: "Status",
        meta: { label: "Status" },
        cell: ({ row }) => <StatusBadge classification={row.original.errorClassification} />,
        size: 110,
      },
      {
        id: "statusCode",
        header: "Code",
        accessorFn: (row) => row.statusCode,
        meta: { label: "Status code" },
        cell: ({ getValue }) => {
          const value = getValue<number | undefined>();
          return <span className="tabular-nums">{value ?? "—"}</span>;
        },
        size: 70,
      },
      {
        id: "userId",
        header: "User ID",
        accessorFn: (row) => row.userId,
        meta: { label: "User ID" },
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate font-mono text-xs">{value || "—"}</span>
              </TooltipTrigger>
              <TooltipContent>{value || "—"}</TooltipContent>
            </Tooltip>
          );
        },
        size: 160,
      },
      {
        id: "route",
        header: "Route",
        accessorFn: (row) => (routeGroupingEnabled ? row.normalizedRoute : row.route),
        meta: { label: "Route" },
        cell: ({ row }) => {
          const displayRoute = routeGroupingEnabled ? row.original.normalizedRoute : row.original.route;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate font-mono text-xs">{displayRoute || "—"}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>API: {row.original.route || "—"}</p>
                <p className="text-muted-foreground">Normalized: {row.original.normalizedRoute || "—"}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
        size: 220,
      },
      {
        id: "method",
        header: "Method",
        accessorFn: (row) => row.method,
        meta: { label: "Method" },
        cell: ({ getValue }) => <span className="text-xs font-medium">{getValue<string>()}</span>,
        size: 80,
      },
      {
        id: "errorMessage",
        header: "Error message",
        meta: { label: "Error message" },
        cell: ({ row }) => {
          const value = row.original.errorMessage;
          if (!value) return <span className="text-muted-foreground">—</span>;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate text-xs">{value}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-80">{value}</TooltipContent>
            </Tooltip>
          );
        },
        size: 240,
      },
      {
        id: "createdAt",
        header: "Created",
        meta: { label: "Created at" },
        cell: ({ row }) => <TimestampCell value={row.original.createdAt} />,
        size: 170,
      },
      {
        id: "updatedAt",
        header: "Updated",
        meta: { label: "Updated at" },
        cell: ({ row }) => <TimestampCell value={row.original.updatedAt} />,
        size: 170,
      },
      {
        id: "responseStored",
        header: "Stored",
        meta: { label: "Response stored" },
        cell: ({ row }) => (
          <span
            role="img"
            aria-label={row.original.responseStored ? "Response stored" : "Response not stored"}
            className={cn("text-xs", row.original.responseStored ? "text-success" : "text-muted-foreground")}
          >
            {row.original.responseStored ? "Yes" : "No"}
          </span>
        ),
        size: 70,
      },
      {
        id: "isFixed",
        header: "Fixed",
        meta: { label: "Fixed status" },
        cell: ({ row }) => (
          <span
            role="img"
            aria-label={row.original.isFixed ? "Fixed" : "Not fixed"}
            className={cn("text-xs", row.original.isFixed ? "text-success" : "text-muted-foreground")}
          >
            {row.original.isFixed ? "Fixed" : "Not fixed"}
          </span>
        ),
        size: 90,
      },
      {
        id: "id",
        header: "Request ID",
        meta: { label: "Request ID" },
        cell: ({ row }) => <span className="block truncate font-mono text-xs">{row.original.id}</span>,
        size: 140,
      },
      {
        id: "actions",
        header: "Actions",
        meta: { label: "Actions" },
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label="View details"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick(row.original.id);
                  }}
                >
                  <Eye className="size-3.5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View details</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label="Copy options"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Copy className="size-3.5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onSelect={() => copy(row.original.userId, "User ID")}>Copy user ID</DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    copy(
                      routeGroupingEnabled ? row.original.normalizedRoute : row.original.route,
                      routeGroupingEnabled ? "Normalized route" : "Raw route",
                    )
                  }
                >
                  Copy {routeGroupingEnabled ? "normalized route" : "raw route"}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => copy(row.original.id, "Request ID")}>Copy request ID</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => copy(row.original.inputRaw, "Raw input")}>Copy raw input</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => copy(row.original.responseRaw, "Raw response")}>
                  Copy raw response
                </DropdownMenuItem>
                {row.original.errorMessage ? (
                  <DropdownMenuItem onSelect={() => copy(row.original.errorMessage as string, "Error message")}>
                    Copy error message
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
        size: 90,
      },
    ],
    [copy, onRowClick, routeGroupingEnabled],
  );

  const sorting: SortingState = sortBy ? [{ id: sortBy, desc: sortDirection !== "asc" }] : [];

  const table = useReactTable({
    data: records,
    columns,
    state: { sorting, columnVisibility },
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    manualSorting: true,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (first && SORTABLE_COLUMNS.has(first.id as SortableField)) {
        onSortChange(first.id as SortableField, first.desc ? "desc" : "asc");
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;
  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0) : 0;

  const handleDownloadCsv = () => {
    if (records.length === 0) return;
    try {
      downloadRecordsCsv(records);
      toast.success(`Downloaded ${records.length} record${records.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {resultMayBePartial ? (
            <span className="flex items-center gap-1 text-xs text-warning" role="status">
              <AlertTriangle className="size-3.5" aria-hidden="true" />
              This result may be partial for the current filter combination.
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button asChild type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
            <Link href="/route-analytics" aria-label="Open Route Analytics grouped by route">
              <Waypoints className="size-3.5" aria-hidden="true" />
              Group by Route
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={handleDownloadCsv}
            disabled={isLoading || records.length === 0}
            aria-label="Download displayed records as CSV"
          >
            <Download className="size-3.5" aria-hidden="true" />
            Download CSV
          </Button>
          <ColumnVisibilityMenu table={table} />
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border">
        {isLoading ? <CircularLoading overlay label="Loading logs…" size="lg" /> : null}
        {error ? (
          <div className="absolute inset-0 z-20 flex min-h-[12rem] items-center justify-center bg-background/85 p-6 backdrop-blur-[1px]">
            <ErrorState title="Could not load logs" onRetry={onRetry} />
          </div>
        ) : null}
        <div
          ref={parentRef}
          className={cn("absolute inset-0 overflow-auto", (isLoading || error) && "opacity-50")}
          tabIndex={0}
          aria-label="Logs table"
        >
          <table
            className="caption-bottom text-sm"
            style={{ width: table.getTotalSize(), tableLayout: "fixed" }}
          >
            <thead className="sticky top-0 z-10 bg-background">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => {
                    const isSortable = SORTABLE_COLUMNS.has(header.column.id as SortableField);
                    const isSorted = sortBy === header.column.id;
                    const size = header.getSize();
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        className="relative select-none overflow-hidden whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground"
                        style={{ width: size, maxWidth: size }}
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            className="flex items-center gap-1 outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            onClick={() =>
                              onSortChange(
                                header.column.id as SortableField,
                                isSorted && sortDirection === "desc" ? "asc" : "desc",
                              )
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {isSorted ? (
                              sortDirection === "asc" ? (
                                <ArrowUp className="size-3" aria-hidden="true" />
                              ) : (
                                <ArrowDown className="size-3" aria-hidden="true" />
                              )
                            ) : (
                              <ArrowUpDown className="size-3 opacity-40" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                        {header.column.getCanResize() ? (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none hover:bg-primary/40"
                          />
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading && rows.length === 0
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      {columns.map((col) => (
                        <td key={col.id} className="p-2">
                          <div className="h-5 w-full animate-pulse rounded bg-muted" />
                        </td>
                      ))}
                    </tr>
                  ))
                : null}

              {paddingTop > 0 ? (
                <tr aria-hidden="true">
                  <td style={{ height: paddingTop }} colSpan={columns.length} />
                </tr>
              ) : null}

              {!error && (!isLoading || rows.length > 0)
                ? virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    if (!row) return null;
                    return (
                      <tr
                        key={row.id}
                        tabIndex={0}
                        className="cursor-pointer border-b outline-none last:border-0 hover:bg-accent/40 focus-visible:bg-accent/60"
                        onClick={() => onRowClick(row.original.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row.original.id);
                          }
                        }}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const size = cell.column.getSize();
                          return (
                            <td
                              key={cell.id}
                              className="overflow-hidden px-2 py-2 align-middle"
                              style={{ width: size, maxWidth: size }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                : null}

              {paddingBottom > 0 ? (
                <tr aria-hidden="true">
                  <td style={{ height: paddingBottom }} colSpan={columns.length} />
                </tr>
              ) : null}
            </tbody>
          </table>

          {!isLoading && !error && rows.length === 0 ? (
            <div className="p-6">
              <EmptyState title="No logs match the current filters" description="Try clearing or adjusting filters to see more results." />
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-1 items-center gap-3 text-xs text-muted-foreground sm:grid-cols-[1fr_auto_1fr]">
        <div className="flex items-center gap-2 sm:justify-self-start">
          <span>Page size</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number.parseInt(v, 10))}>
            <SelectTrigger size="sm" className="h-7 w-20 text-xs" aria-label="Page size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex items-center justify-center gap-2 justify-self-center" aria-label="Pagination">
          {isPageCountLoading ? (
            <CircularLoading label="Resolving pages…" size="sm" className="gap-1.5" />
          ) : (
            <>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!hasPreviousPage || isLoading}
                onClick={onPreviousPage}
                aria-label="Previous page"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </Button>

              {buildPaginationItems(currentPage, knownPageCount).map((item, index) =>
                item === "ellipsis" ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="flex size-8 items-center justify-center text-muted-foreground"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    variant={item === currentPage ? "default" : "outline"}
                    size="icon"
                    className="size-8 text-xs"
                    disabled={isLoading}
                    aria-label={`Page ${item}`}
                    aria-current={item === currentPage ? "page" : undefined}
                    onClick={() => onGoToPage(item)}
                  >
                    {item}
                  </Button>
                ),
              )}

              <Button
                variant="outline"
                size="icon"
                className="size-8"
                disabled={!hasNextPage || isLoading}
                onClick={onNextPage}
                aria-label="Next page"
              >
                <ChevronRight className="size-4" aria-hidden="true" />
              </Button>
            </>
          )}
        </nav>

        <div className="hidden sm:block" aria-hidden="true" />
      </div>
    </div>
  );
}
